/** Shared mega-menu navigation supports configured routes, mobile panels, and keyboard navigation. */

import Component from '@glimmer/component';
import { service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import { cached, tracked } from '@glimmer/tracking';
import { modifier } from 'ember-modifier';
import { LinkTo } from '@ember/routing';
import { PhCaretDown, PhCaretRight, PhDotsThree, PhList, PhX } from 'ember-phosphor-icons';
import type { ComponentLike } from '@glint/template';

/**
 * Modifier that keeps a wide dropdown panel inside the viewport: if the
 * panel overflows the right edge, shift it left (down to the left margin).
 */
const clampPanel = modifier((element: HTMLElement) => {
  const margin = 16;
  const clamp = () => {
    element.style.marginLeft = '';
    const rect = element.getBoundingClientRect();
    const overflowRight = rect.right - (window.innerWidth - margin);
    if (overflowRight > 0) {
      const shift = Math.min(overflowRight, Math.max(rect.left - margin, 0));
      element.style.marginLeft = `-${shift}px`;
    }
  };
  clamp();
  window.addEventListener('resize', clamp);
  return () => window.removeEventListener('resize', clamp);
});

/**
 * Configuration for a flyout submenu item (leaf node)
 */
export interface MegaMenuFlyoutItem {
  /** Phosphor icon component */
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  /** Display label */
  label: string;
  /** Short description shown below label */
  description: string;
  /** Ember route to navigate to */
  route: string;
}

/**
 * Configuration for a dropdown menu item with a direct route
 */
export interface MegaMenuDropdownItemWithRoute {
  /** Phosphor icon component */
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  /** Display label */
  label: string;
  /** Short description shown below label */
  description: string;
  /** Ember route to navigate to */
  route: string;
  /** Optional stat/badge to display (e.g., "$125/mo", "84/120") */
  stat?: string;
  /** No flyout for route items */
  flyout?: never;
  /** Optional visibility predicate - return false to hide */
  isVisible?: boolean;
}

/**
 * Configuration for a dropdown menu item with a flyout submenu
 */
export interface MegaMenuDropdownItemWithFlyout {
  /** Phosphor icon component */
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  /** Display label */
  label: string;
  /** Short description shown below label */
  description: string;
  /** No direct route for flyout items */
  route?: never;
  /** Optional stat/badge to display */
  stat?: string;
  /** Nested flyout submenu */
  flyout: MegaMenuFlyoutItem[];
  /** Optional visibility predicate - return false to hide */
  isVisible?: boolean;
}

/**
 * Configuration for a dropdown menu item (either route or flyout)
 */
export type MegaMenuDropdownItem = MegaMenuDropdownItemWithRoute | MegaMenuDropdownItemWithFlyout;

/**
 * Navigation item with a direct route (no dropdown)
 */
export interface MegaMenuItemWithRoute {
  /** Unique identifier for the nav item */
  id: string;
  /** Phosphor icon component */
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  /** Display label */
  label: string;
  /** Ember route to navigate to */
  route: string;
  /** Never has a dropdown */
  dropdown?: never;
  /** Optional visibility predicate - return false to hide */
  isVisible?: boolean;
  /** Push this item (and everything after it) to the end of the bar */
  alignEnd?: boolean;
  /** Render only the icon in the bar (label becomes the accessible name) */
  iconOnly?: boolean;
  /** Fold this item into the desktop bar's "More" dropdown (mobile keeps it inline) */
  collapseInBar?: boolean;
}

/**
 * Navigation item that performs an action instead of transitioning routes.
 */
export interface MegaMenuItemWithAction {
  /** Unique identifier for the nav item */
  id: string;
  /** Phosphor icon component */
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  /** Display label */
  label: string;
  /** Callback to run when selected */
  onClick: () => void;
  /** Never has a direct route */
  route?: never;
  /** Never has a dropdown */
  dropdown?: never;
  /** Optional visibility predicate - return false to hide */
  isVisible?: boolean;
  /** Push this item (and everything after it) to the end of the bar */
  alignEnd?: boolean;
  /** Render only the icon in the bar (label becomes the accessible name) */
  iconOnly?: boolean;
  /** Action items never collapse — the "More" panel only renders links */
  collapseInBar?: never;
}

/**
 * Navigation item with a dropdown menu
 */
export interface MegaMenuItemWithDropdown {
  /** Unique identifier for the nav item */
  id: string;
  /** Phosphor icon component */
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  /** Display label */
  label: string;
  /** Never has a direct route */
  route?: never;
  /** Dropdown menu items */
  dropdown: MegaMenuDropdownItem[];
  /** Optional route for the "View Dashboard" CTA (overrides auto-detection) */
  indexRoute?: string;
  /** Optional visibility predicate - return false to hide */
  isVisible?: boolean;
  /** Push this item (and everything after it) to the end of the bar */
  alignEnd?: boolean;
  /** Fold this item into the desktop bar's "More" dropdown (mobile keeps it inline) */
  collapseInBar?: boolean;
}

export type MegaMenuItem =
  MegaMenuItemWithRoute | MegaMenuItemWithDropdown | MegaMenuItemWithAction;

export interface MegaMenuSignature {
  Args: {
    /** Navigation items configuration */
    items: MegaMenuItem[];
    /** Whether layout is fullscreen (affects container width) */
    isFullscreen?: boolean;
    /** Optional logo/brand component to render */
    logo?: ComponentLike<{ Element: HTMLElement }>;
    /** Label for the mobile brand display */
    brandLabel?: string;
    /** Label for the synthesized overflow item that holds `collapseInBar` items */
    moreLabel?: string;
    /**
     * Let the desktop bar span the full viewport width (padding only) instead
     * of the capped, centered `max-w-7xl` wrapper. Use for nav bars that should
     * use all available width so items only collapse into "More" on genuinely
     * narrow windows rather than at the ~1280px cap.
     */
    fullWidthBar?: boolean;
    /** Render dropdowns as a two-step section picker instead of a multi-column mega panel. */
    twoStep?: boolean;
  };
  Blocks: {
    /** Optional trailing desktop bar content, such as a global search trigger */
    default?: [];
    /**
     * Optional actions rendered in the mobile top bar, immediately left of the
     * hamburger toggle (e.g. a global search trigger) so they stay reachable on
     * touch without opening the menu panel.
     */
    mobileActions?: [];
  };
  Element: HTMLElement;
}

/**
 * Helper to filter visible dropdown items
 */
const getVisibleDropdownItems = (items: MegaMenuDropdownItem[]): MegaMenuDropdownItem[] => {
  return items.filter((item) => item.isVisible !== false);
};

/** A leaf link inside a dropdown panel, with its flattened roving-tab index. */
interface PanelLeaf {
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  label: string;
  description: string;
  route: string;
  stat?: string;
  flatIndex: number;
}

/** A column in the dropdown panel: ungrouped links (label null) or a titled section. */
interface PanelGroup {
  key: string;
  label: string | null;
  items: PanelLeaf[];
}

const QUICK_LINKS_GROUP_KEY = '__quick-links__';

/**
 * Flatten a dropdown config into panel columns: route items form an untitled
 * leading column; each flyout item becomes a titled section column with its
 * children inlined. Sections render side by side (a true mega panel) instead
 * of hover-nested flyouts.
 */
const getPanelGroups = (items: MegaMenuDropdownItem[]): PanelGroup[] => {
  const visible = getVisibleDropdownItems(items);
  const flat: PanelLeaf[] = [];
  const sections: PanelGroup[] = [];
  for (const [index, item] of visible.entries()) {
    if (item.flyout) {
      sections.push({
        key: `section-${index}`,
        label: item.label,
        items: item.flyout.map((leaf) => ({ ...leaf, flatIndex: -1 })),
      });
    } else {
      flat.push({ ...item, flatIndex: -1 });
    }
  }
  const groups = [
    ...(flat.length ? [{ key: QUICK_LINKS_GROUP_KEY, label: null, items: flat }] : []),
    ...sections,
  ];
  let i = 0;
  for (const group of groups) {
    for (const leaf of group.items) {
      leaf.flatIndex = i++;
    }
  }
  return groups;
};

/** True when a dropdown renders as a multi-column (wide) panel. */
const isWidePanel = (items: MegaMenuDropdownItem[]): boolean => {
  return getPanelGroups(items).length > 1;
};

/**
 * Merge `collapseInBar` items into one dropdown config for the synthesized
 * "More" bar item. Dropdown items contribute their sections (an item with an
 * index route keeps a plain link row so its landing page stays reachable);
 * simple route items become plain link rows in the untitled leading column.
 */
const buildMoreDropdown = (items: MegaMenuItem[]): MegaMenuDropdownItem[] => {
  const merged: MegaMenuDropdownItem[] = [];
  for (const item of items) {
    if (item.dropdown) {
      if (item.indexRoute) {
        merged.push({
          icon: item.icon,
          label: item.label,
          description: '',
          route: item.indexRoute,
        });
      }
      merged.push(...item.dropdown);
    } else if (item.route) {
      merged.push({ icon: item.icon, label: item.label, description: '', route: item.route });
    }
  }
  return merged;
};

/**
 * MegaMenu - Full mega-menu navigation component
 *
 * Features:
 * - Desktop: Horizontal nav with mega-menu dropdowns; `collapseInBar` items
 *   fold into a synthesized "More" dropdown so the bar fits its wrapper
 * - Mobile: Hamburger menu with slide-out panel and accordions
 * - Full keyboard navigation (arrow keys, Home/End, Escape)
 * - Dark mode support
 */
export class MegaMenu extends Component<MegaMenuSignature> {
  @service declare router: RouterService;

  @tracked openDropdownId: string | null = null;

  /** The bar trigger lights up when the current route lives inside its menu. */
  isItemActive = (item: MegaMenuItem): boolean => {
    const current = this.router.currentRouteName ?? '';
    const matches = (route?: string) => {
      if (!route) return false;
      const base = route.replace(/\.index$/, '');
      return current === route || current === base || current.startsWith(base + '.');
    };
    if (item.route) return matches(item.route);
    return (item.dropdown ?? []).some((d) =>
      d.flyout ? d.flyout.some((leaf) => matches(leaf.route)) : matches(d.route)
    );
  };

  @tracked isMobileMenuOpen = false;
  @tracked mobileExpandedId: string | null = null;
  @tracked mobileExpandedFlyout: string | null = null;
  @tracked selectedTwoStepGroupKey: string | null = null;
  @tracked mobileTwoStepGroupKey: string | null = null;
  @tracked focusedMenuIndex = -1;

  /** Available width of the desktop bar (tracked via ResizeObserver). */
  @tracked barWidth = 0;
  /** Natural width of brand + every nav item at full size (hidden measurer). */
  @tracked naturalItemsWidth = 0;
  /** Width of the trailing yielded content (e.g. the global search trigger). */
  @tracked trailingWidth = 0;
  /** Column gap between bar items, in px. */
  @tracked barGap = 0;

  /**
   * Observe the real bar's available width + gap. `collapseInBar` items only
   * fold into "More" when the full item row would actually overflow this width
   * (see `shouldCollapse`), so a wide viewport keeps every item inline.
   */
  observeBar = modifier((element: HTMLElement) => {
    const update = () => {
      this.barWidth = element.clientWidth;
      const styles = getComputedStyle(element);
      this.barGap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  });

  /** Measure the natural width of brand + all items from the hidden measurer. */
  measureItems = modifier((element: HTMLElement) => {
    // Force the measurer into a single off-screen row (set via JS, not the
    // stylesheet, so `scrollWidth` reflects the natural content width even where
    // the addon CSS isn't loaded — e.g. component tests).
    Object.assign(element.style, {
      position: 'absolute',
      top: '0',
      // Off-screen to the left so this wide, always-present element never
      // extends the page's horizontal scroll area.
      left: '-9999px',
      display: 'flex',
      width: 'max-content',
      visibility: 'hidden',
      pointerEvents: 'none',
      whiteSpace: 'nowrap',
    });
    const update = () => (this.naturalItemsWidth = element.scrollWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  });

  /** Measure the trailing (yielded) content width in the real bar. */
  measureTrailing = modifier((element: HTMLElement) => {
    const update = () => (this.trailingWidth = element.offsetWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  });

  /**
   * True when the full item row + trailing content can't fit the available bar
   * width, so `collapseInBar` items should fold into "More". Until the widths
   * are measured (initial render), stay expanded. A 1px tolerance avoids
   * collapsing on sub-pixel rounding.
   */
  get shouldCollapse(): boolean {
    if (!this.barWidth || !this.naturalItemsWidth) return false;
    // Only reserve a gap for trailing content when there actually is some, so
    // a bar with no default block doesn't collapse a gap-width too early.
    const trailing = this.trailingWidth > 0 ? this.barGap + this.trailingWidth : 0;
    return this.naturalItemsWidth + trailing > this.barWidth + 1;
  }

  @cached
  get visibleNavItems() {
    return this.args.items.filter((item) => item.isVisible !== false);
  }

  /**
   * Items rendered on the desktop bar: `collapseInBar` items fold into a
   * single synthesized "More" dropdown (placed where the first collapsed
   * item sat) so the bar fits narrow desktop viewports and the capped
   * wrapper width. The mobile panel keeps the full item list.
   */
  @cached
  get barItems(): MegaMenuItem[] {
    const collapsed = this.visibleNavItems.filter((item) => item.collapseInBar);
    if (!collapsed.length || !this.shouldCollapse) return this.visibleNavItems;
    const moreItem: MegaMenuItemWithDropdown = {
      id: 'tio-mega-menu-more',
      icon: PhDotsThree,
      label: this.args.moreLabel ?? 'More',
      // Inherit bar-position semantics from the first collapsed item so a
      // right-aligned item collapses into a right-aligned More trigger.
      alignEnd: collapsed[0]?.alignEnd,
      dropdown: buildMoreDropdown(collapsed),
    };
    const bar: MegaMenuItem[] = [];
    let morePlaced = false;
    for (const item of this.visibleNavItems) {
      if (!item.collapseInBar) {
        bar.push(item);
      } else if (!morePlaced) {
        bar.push(moreItem);
        morePlaced = true;
      }
    }
    return bar;
  }

  get brandLabel() {
    return this.args.brandLabel ?? 'TIO';
  }

  toggleDropdown = (id: string, event?: MouseEvent) => {
    if (this.openDropdownId === id) {
      this.openDropdownId = null;
    } else {
      this.openDropdownId = id;
    }
    this.selectedTwoStepGroupKey = null;
    // Safari doesn't focus <button> on click. Re-focus the trigger after
    // Glimmer re-renders so keyboard events (ArrowDown, Escape) fire on it.
    if (event) {
      const btn = event.currentTarget as HTMLElement;
      requestAnimationFrame(() => btn?.focus());
    }
  };

  closeDropdown = () => {
    this.openDropdownId = null;
    this.selectedTwoStepGroupKey = null;
  };

  // Deferred close for links - prevents interference with LinkTo navigation
  handleLinkClick = () => {
    setTimeout(() => {
      this.openDropdownId = null;
      this.isMobileMenuOpen = false;
      this.mobileExpandedId = null;
      this.mobileExpandedFlyout = null;
      this.selectedTwoStepGroupKey = null;
      this.mobileTwoStepGroupKey = null;
    }, 0);
  };

  handleActionClick = (item: MegaMenuItem) => {
    if ('onClick' in item) {
      item.onClick();
    }
    this.handleLinkClick();
  };

  toggleMobileMenu = () => {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    if (!this.isMobileMenuOpen) {
      this.mobileExpandedId = null;
      this.mobileExpandedFlyout = null;
      this.mobileTwoStepGroupKey = null;
    }
  };

  closeMobileMenu = () => {
    this.isMobileMenuOpen = false;
    this.mobileExpandedId = null;
    this.mobileExpandedFlyout = null;
    this.mobileTwoStepGroupKey = null;
  };

  toggleMobileExpanded = (id: string) => {
    if (this.mobileExpandedId === id) {
      this.mobileExpandedId = null;
    } else {
      this.mobileExpandedId = id;
    }
    this.mobileExpandedFlyout = null;
    this.mobileTwoStepGroupKey = null;
  };

  toggleMobileFlyout = (label: string) => {
    if (this.mobileExpandedFlyout === label) {
      this.mobileExpandedFlyout = null;
    } else {
      this.mobileExpandedFlyout = label;
    }
  };

  twoStepGroupLabel = (group: PanelGroup) => group.label ?? 'Quick links';

  destinationCountLabel = (count: number) =>
    `${count} ${count === 1 ? 'destination' : 'destinations'}`;

  mobileBackLabel = (label: string) => `Back to ${label}`;

  twoStepGroupIsSelected = (group: PanelGroup) => {
    return group.key === this.currentTwoStepGroup?.key;
  };

  selectTwoStepGroup = (group: PanelGroup) => {
    this.selectedTwoStepGroupKey = group.key;
    this.focusedMenuIndex = 0;
    requestAnimationFrame(() => this.focusMenuItem(0));
  };

  focusTwoStepGroup = (index: number) => {
    const groups = this.currentTwoStepGroups;
    if (groups.length === 0) return;

    const nextIndex = (index + groups.length) % groups.length;
    const group = groups[nextIndex];
    if (!group) return;

    this.selectedTwoStepGroupKey = group.key;
    requestAnimationFrame(() => {
      const buttons = document.querySelectorAll('.tio-mega-menu__two-step-group');
      (buttons[nextIndex] as HTMLElement | undefined)?.focus();
    });
  };

  handleTwoStepGroupKeyDown = (index: number, event: KeyboardEvent) => {
    const groups = this.currentTwoStepGroups;
    const group = groups[index];
    if (!group || groups.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusTwoStepGroup(index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusTwoStepGroup(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        this.focusTwoStepGroup(0);
        break;
      case 'End':
        event.preventDefault();
        this.focusTwoStepGroup(groups.length - 1);
        break;
      case 'ArrowRight':
      case 'Enter':
      case ' ': {
        event.preventDefault();
        this.selectTwoStepGroup(group);
        break;
      }
      case 'Escape':
        event.preventDefault();
        this.closeDropdown();
        (document.querySelector('button[aria-expanded="true"]') as HTMLElement | null)?.focus();
        break;
    }
  };

  selectMobileTwoStepGroup = (group: PanelGroup) => {
    this.mobileTwoStepGroupKey = group.key;
    requestAnimationFrame(() => {
      (
        document.querySelector('.tio-mega-menu__two-step-mobile-leaf') as HTMLElement | null
      )?.focus();
    });
  };

  focusMobileTwoStepGroup = (index: number) => {
    const groups = this.currentMobileTwoStepGroups;
    if (groups.length === 0) return;

    const nextIndex = (index + groups.length) % groups.length;
    if (!groups[nextIndex]) return;

    requestAnimationFrame(() => {
      const buttons = document.querySelectorAll('.tio-mega-menu__two-step-mobile-group');
      (buttons[nextIndex] as HTMLElement | undefined)?.focus();
    });
  };

  handleMobileTwoStepGroupKeyDown = (index: number, event: KeyboardEvent) => {
    const groups = this.currentMobileTwoStepGroups;
    const group = groups[index];
    if (!group || groups.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusMobileTwoStepGroup(index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusMobileTwoStepGroup(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        this.focusMobileTwoStepGroup(0);
        break;
      case 'End':
        event.preventDefault();
        this.focusMobileTwoStepGroup(groups.length - 1);
        break;
      case 'ArrowRight':
      case 'Enter':
      case ' ': {
        event.preventDefault();
        this.selectMobileTwoStepGroup(group);
        break;
      }
      case 'Escape':
        event.preventDefault();
        this.backMobileTwoStepGroup();
        break;
    }
  };

  backMobileTwoStepGroup = () => {
    this.mobileTwoStepGroupKey = null;
  };

  get currentTwoStepGroups() {
    const openItem = this.barItems.find((item) => item.id === this.openDropdownId);
    return getPanelGroups(openItem?.dropdown ?? []);
  }

  get currentTwoStepGroup() {
    const groups = this.currentTwoStepGroups;
    return groups.find((group) => group.key === this.selectedTwoStepGroupKey) ?? groups[0];
  }

  get mobileExpandedItem() {
    return this.visibleNavItems.find((item) => item.id === this.mobileExpandedId);
  }

  get currentMobileTwoStepGroups() {
    return getPanelGroups(this.mobileExpandedItem?.dropdown ?? []);
  }

  get currentMobileTwoStepGroup() {
    const groups = this.currentMobileTwoStepGroups;
    return groups.find((group) => group.key === this.mobileTwoStepGroupKey) ?? groups[0];
  }

  // Flattened leaf links of the open dropdown panel (sections inlined),
  // in DOM order — drives the roving-tab keyboard navigation.
  get currentDropdownItems() {
    const openItem = this.barItems.find((item) => item.id === this.openDropdownId);
    const items = openItem?.dropdown ?? [];
    return getPanelGroups(items).flatMap((group) => group.items);
  }

  get currentMenuItems() {
    return this.args.twoStep ? (this.currentTwoStepGroup?.items ?? []) : this.currentDropdownItems;
  }

  // Focus a menu item by index using DOM query
  focusMenuItem = (index: number) => {
    const itemCount = this.currentMenuItems.length;
    if (index >= 0 && index < itemCount) {
      this.focusedMenuIndex = index;
      // Query for the menu items in the DOM
      requestAnimationFrame(() => {
        const menuItems = document.querySelectorAll('.tio-mega-menu__panel [role="menuitem"]');
        const item = menuItems[index] as HTMLElement;
        item?.focus();
      });
    }
  };

  handleTriggerKeyDown = (id: string, event: KeyboardEvent) => {
    switch (event.key) {
      case 'Escape':
        this.closeDropdown();
        // Focus the trigger button
        {
          const trigger = (event.target as HTMLElement).closest('button');
          trigger?.focus();
        }
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.toggleDropdown(id);
        // Focus first menu item when opening
        if (this.openDropdownId === id) {
          this.focusedMenuIndex = -1;
          // Wait for render then focus first item
          requestAnimationFrame(() => {
            this.focusMenuItem(0);
          });
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (this.openDropdownId !== id) {
          this.toggleDropdown(id);
          requestAnimationFrame(() => {
            this.focusMenuItem(0);
          });
        } else {
          this.focusMenuItem(0);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (this.openDropdownId !== id) {
          this.toggleDropdown(id);
          requestAnimationFrame(() => {
            const lastIndex = this.currentDropdownItems.length - 1;
            this.focusMenuItem(lastIndex);
          });
        } else {
          const lastIndex = this.currentDropdownItems.length - 1;
          this.focusMenuItem(lastIndex);
        }
        break;
    }
  };

  // Roves across all leaf links, including links inside sections.
  handleMenuKeyDown = (index: number, event: KeyboardEvent) => {
    const itemCount = this.currentMenuItems.length;

    switch (event.key) {
      case 'Escape': {
        event.preventDefault();
        this.closeDropdown();
        const trigger = document.querySelector('button[aria-expanded="true"]') as HTMLElement;
        trigger?.focus();
        break;
      }
      case 'ArrowLeft':
        if (this.args.twoStep) {
          event.preventDefault();
          const groupIndex = this.currentTwoStepGroups.findIndex(
            (group) => group.key === this.currentTwoStepGroup?.key
          );
          this.focusTwoStepGroup(groupIndex < 0 ? 0 : groupIndex);
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.focusMenuItem((index + 1) % itemCount);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusMenuItem(index <= 0 ? itemCount - 1 : index - 1);
        break;
      case 'Home':
        event.preventDefault();
        this.focusMenuItem(0);
        break;
      case 'End':
        event.preventDefault();
        this.focusMenuItem(itemCount - 1);
        break;
      case 'Tab':
        // Allow tab to close menu and move to next element
        this.closeDropdown();
        break;
    }
  };

  <template>
    <nav
      class="tio-mega-menu hidden md:block sticky top-0 z-40"
      role="navigation"
      aria-label="Main navigation"
      ...attributes
    >
      {{#if this.openDropdownId}}
        <button
          type="button"
          class="fixed inset-0 cursor-default"
          aria-label="Close menu"
          tabindex="-1"
          {{on "click" this.closeDropdown}}
        ></button>
      {{/if}}
      <div class="relative {{if (or @isFullscreen @fullWidthBar) 'px-6' 'max-w-7xl mx-auto px-6'}}">
        <div class="tio-mega-menu__bar tio-row-extra-tight" {{this.observeBar}}>
          {{#if @logo}}
            {{#let @logo as |Logo|}}
              <Logo />
            {{/let}}
          {{else}}
            <span class="tio-mega-menu__brand tio-text-sm-semibold mr-3">{{this.brandLabel}}</span>
          {{/if}}
          {{#each this.barItems as |item|}}
            {{#if item.dropdown}}
              <div class="relative {{if item.alignEnd 'ml-auto'}}">
                <button
                  type="button"
                  class="tio-mega-menu__nav-item
                    {{if (eq this.openDropdownId item.id) 'is-open'}}
                    {{if (this.isItemActive item) 'is-current'}}"
                  aria-expanded="{{if (eq this.openDropdownId item.id) 'true' 'false'}}"
                  aria-haspopup="menu"
                  aria-label={{item.label}}
                  title={{item.label}}
                  {{on "click" (fn this.toggleDropdown item.id)}}
                  {{on "keydown" (fn this.handleTriggerKeyDown item.id)}}
                >
                  {{#let item.icon as |Icon|}}
                    <Icon @size={{18}} />
                  {{/let}}
                  <span>{{item.label}}</span>
                  <PhCaretDown
                    @size={{14}}
                    class="transition-transform
                      {{if (eq this.openDropdownId item.id) 'rotate-180'}}"
                  />
                </button>

                {{#if (eq this.openDropdownId item.id)}}
                  {{#if @twoStep}}
                    <div
                      class="tio-mega-menu__panel tio-mega-menu__two-step-panel absolute left-0 top-full mt-2 rounded-xl z-30"
                      role="menu"
                      {{clampPanel}}
                    >
                      <div class="tio-mega-menu__two-step-header">
                        <strong>{{item.label}}</strong>
                        <span>Choose a section, then a destination</span>
                      </div>
                      <div class="tio-mega-menu__two-step-layout">
                        <div
                          class="tio-mega-menu__two-step-groups"
                          role="group"
                          aria-label="Sections"
                        >
                          {{#each this.currentTwoStepGroups as |group groupIndex|}}
                            <button
                              type="button"
                              class="tio-mega-menu__two-step-group
                                {{if (this.twoStepGroupIsSelected group) 'is-selected'}}"
                              aria-pressed="{{if
                                (this.twoStepGroupIsSelected group)
                                'true'
                                'false'
                              }}"
                              {{on "keydown" (fn this.handleTwoStepGroupKeyDown groupIndex)}}
                              {{on "click" (fn this.selectTwoStepGroup group)}}
                            >
                              <span>{{this.twoStepGroupLabel group}}</span>
                              <PhCaretRight @size={{16}} />
                            </button>
                          {{/each}}
                        </div>

                        {{#let this.currentTwoStepGroup as |selectedGroup|}}
                          {{#if selectedGroup}}
                            <div
                              class="tio-mega-menu__two-step-detail"
                              role="group"
                              aria-label={{this.twoStepGroupLabel selectedGroup}}
                            >
                              <div class="tio-mega-menu__two-step-breadcrumb">
                                <span>{{item.label}}</span>
                                <span aria-hidden="true">/</span>
                                <strong>{{this.twoStepGroupLabel selectedGroup}}</strong>
                              </div>
                              <div class="tio-mega-menu__two-step-detail-heading">
                                <strong>{{this.twoStepGroupLabel selectedGroup}}</strong>
                                <span>{{this.destinationCountLabel
                                    selectedGroup.items.length
                                  }}</span>
                              </div>
                              {{#each selectedGroup.items as |leaf leafIndex|}}
                                <LinkTo
                                  @route={{leaf.route}}
                                  class="tio-mega-menu__item tio-mega-menu__two-step-item group"
                                  role="menuitem"
                                  tabindex={{if (eq this.focusedMenuIndex leafIndex) "0" "-1"}}
                                  {{on "click" this.handleLinkClick}}
                                  {{on "keydown" (fn this.handleMenuKeyDown leafIndex)}}
                                >
                                  <div class="tio-mega-menu__icon">
                                    {{#let leaf.icon as |LeafIcon|}}
                                      <LeafIcon @size={{20}} />
                                    {{/let}}
                                  </div>
                                  <div class="tio-fill-min">
                                    <div class="tio-row-tight">
                                      <span class="tio-mega-menu__title">{{leaf.label}}</span>
                                      {{#if leaf.stat}}
                                        <span
                                          class="tio-mega-menu__badge tio-text-xs-medium px-2 py-0.5 rounded-full"
                                        >{{leaf.stat}}</span>
                                      {{/if}}
                                    </div>
                                    <p class="tio-mega-menu__desc mt-0.5">{{leaf.description}}</p>
                                  </div>
                                </LinkTo>
                              {{/each}}
                            </div>
                          {{/if}}
                        {{/let}}
                      </div>

                      {{#if item.indexRoute}}
                        <div class="tio-mega-menu__cta p-3">
                          <LinkTo
                            @route={{item.indexRoute}}
                            class="tio-row-center gap-2 tio-mega-menu__cta-link w-full py-2 px-4 tio-text-sm-medium rounded-lg"
                            {{on "click" this.handleLinkClick}}
                          >
                            View
                            {{item.label}}
                            Dashboard →
                          </LinkTo>
                        </div>
                      {{/if}}
                    </div>
                  {{else}}
                    <div
                      class="tio-mega-menu__panel absolute left-0 top-full mt-2 rounded-xl z-30
                        {{if (isWidePanel item.dropdown) 'tio-mega-menu__panel--wide' 'w-80'}}"
                      role="menu"
                      {{clampPanel}}
                    >
                      <div class="tio-mega-menu__columns p-2">
                        {{#each (getPanelGroups item.dropdown) as |group|}}
                          <div
                            class="tio-mega-menu__column"
                            role="group"
                            aria-label={{if group.label group.label item.label}}
                          >
                            {{#if group.label}}
                              <div
                                class="tio-text-overline tio-fg-muted px-3 pt-2 pb-1"
                              >{{group.label}}</div>
                            {{/if}}
                            {{#each group.items as |leaf|}}
                              <LinkTo
                                @route={{leaf.route}}
                                class="tio-mega-menu__item group"
                                role="menuitem"
                                tabindex={{if (eq this.focusedMenuIndex leaf.flatIndex) "0" "-1"}}
                                {{on "click" this.handleLinkClick}}
                                {{on "keydown" (fn this.handleMenuKeyDown leaf.flatIndex)}}
                              >
                                <div class="tio-mega-menu__icon">
                                  {{#let leaf.icon as |LeafIcon|}}
                                    <LeafIcon @size={{20}} />
                                  {{/let}}
                                </div>
                                <div class="tio-fill-min">
                                  <div class="tio-row-tight">
                                    <span class="tio-mega-menu__title">{{leaf.label}}</span>
                                    {{#if leaf.stat}}
                                      <span
                                        class="tio-mega-menu__badge tio-text-xs-medium px-2 py-0.5 rounded-full"
                                      >{{leaf.stat}}</span>
                                    {{/if}}
                                  </div>
                                  <p class="tio-mega-menu__desc mt-0.5">{{leaf.description}}</p>
                                </div>
                              </LinkTo>
                            {{/each}}
                          </div>
                        {{/each}}
                      </div>

                      {{#if item.indexRoute}}
                        <div class="tio-mega-menu__cta p-3">
                          <LinkTo
                            @route={{item.indexRoute}}
                            class="tio-row-center gap-2 tio-mega-menu__cta-link w-full py-2 px-4 tio-text-sm-medium rounded-lg"
                            {{on "click" this.handleLinkClick}}
                          >
                            View
                            {{item.label}}
                            Dashboard →
                          </LinkTo>
                        </div>
                      {{/if}}
                    </div>
                  {{/if}}
                {{/if}}
              </div>
            {{else if item.route}}
              <LinkTo
                @route={{item.route}}
                class="tio-mega-menu__nav-item
                  {{if item.alignEnd 'ml-auto'}}
                  {{if (this.isItemActive item) 'is-current'}}"
                aria-label={{item.label}}
                title={{item.label}}
              >
                {{#let item.icon as |Icon|}}
                  <Icon @size={{18}} />
                {{/let}}
                {{#unless item.iconOnly}}
                  <span>{{item.label}}</span>
                {{/unless}}
              </LinkTo>
            {{else}}
              <button
                type="button"
                class="tio-mega-menu__nav-item {{if item.alignEnd 'ml-auto'}}"
                aria-label={{item.label}}
                title={{item.label}}
                {{on "click" (fn this.handleActionClick item)}}
              >
                {{#let item.icon as |Icon|}}
                  <Icon @size={{18}} />
                {{/let}}
                {{#unless item.iconOnly}}
                  <span>{{item.label}}</span>
                {{/unless}}
              </button>
            {{/if}}
          {{/each}}
          {{#if (has-block)}}
            <span class="tio-mega-menu__bar-trailing" {{this.measureTrailing}}>{{yield}}</span>
          {{/if}}
        </div>
      </div>
    </nav>

    {{! Measure outside navigation and the accessibility tree; overflow moves collapseInBar items into More. }}
    <div
      class="tio-mega-menu__bar tio-row-extra-tight tio-mega-menu__measure"
      aria-hidden="true"
      {{this.measureItems}}
    >
      {{#if @logo}}
        {{#let @logo as |Logo|}}
          <Logo />
        {{/let}}
      {{else}}
        <span class="tio-mega-menu__brand tio-text-sm-semibold mr-3">{{this.brandLabel}}</span>
      {{/if}}
      {{#each this.visibleNavItems as |item|}}
        <span class="tio-mega-menu__nav-item">
          {{#let item.icon as |Icon|}}
            <Icon @size={{18}} />
          {{/let}}
          {{#if item.dropdown}}
            <span>{{item.label}}</span>
            <PhCaretDown @size={{14}} />
          {{else}}
            {{#unless item.iconOnly}}
              <span>{{item.label}}</span>
            {{/unless}}
          {{/if}}
        </span>
      {{/each}}
    </div>

    <nav
      class="tio-mega-menu md:hidden sticky top-0 z-40"
      role="navigation"
      aria-label="Mobile navigation"
    >
      <div class="tio-row-between px-4 py-3">
        <div class="tio-row-tight">
          {{#if @logo}}
            {{#let @logo as |Logo|}}
              <Logo />
            {{/let}}
          {{/if}}
          <span class="tio-fg-primary font-bold">{{this.brandLabel}}</span>
        </div>

        <div class="tio-row-tight">
          {{yield to="mobileActions"}}
          <button
            type="button"
            class="tio-mega-menu__icon-btn"
            aria-label="{{if this.isMobileMenuOpen 'Close menu' 'Open menu'}}"
            aria-expanded="{{if this.isMobileMenuOpen 'true' 'false'}}"
            {{on "click" this.toggleMobileMenu}}
          >
            {{#if this.isMobileMenuOpen}}
              <PhX @size={{24}} />
            {{else}}
              <PhList @size={{24}} />
            {{/if}}
          </button>
        </div>
      </div>

      {{#if this.isMobileMenuOpen}}
        <button
          type="button"
          class="tio-mega-menu__backdrop fixed inset-0 z-30"
          aria-label="Close menu"
          {{on "click" this.closeMobileMenu}}
        ></button>

        <div
          class="tio-mega-menu__mobile-panel fixed top-0 right-0 bottom-0 w-80 max-w-[calc(100vw-48px)] z-40 shadow-xl overflow-y-auto"
        >
          <div class="tio-row-between tio-mega-menu__mobile-header px-4 py-3">
            <span class="tio-fg-primary font-bold">Menu</span>
            <button
              type="button"
              class="tio-mega-menu__icon-btn"
              aria-label="Close menu"
              {{on "click" this.closeMobileMenu}}
            >
              <PhX @size={{20}} />
            </button>
          </div>

          <div class="py-2">
            {{#each this.visibleNavItems as |item|}}
              {{#if item.dropdown}}
                <div class="tio-mega-menu__mobile-divider">
                  <button
                    type="button"
                    class="tio-row-between w-full px-4 py-3 text-left"
                    aria-expanded="{{if (eq this.mobileExpandedId item.id) 'true' 'false'}}"
                    {{on "click" (fn this.toggleMobileExpanded item.id)}}
                  >
                    <div class="tio-row">
                      {{#let item.icon as |Icon|}}
                        <Icon @size={{20}} class="tio-fg-muted" />
                      {{/let}}
                      <span class="tio-fg-primary font-medium">{{item.label}}</span>
                    </div>
                    <PhCaretDown
                      @size={{16}}
                      class="tio-mega-menu__caret transition-transform
                        {{if (eq this.mobileExpandedId item.id) 'rotate-180'}}"
                    />
                  </button>

                  {{#if (eq this.mobileExpandedId item.id)}}
                    {{#if @twoStep}}
                      <div class="tio-mega-menu__two-step-mobile">
                        {{#if this.mobileTwoStepGroupKey}}
                          <div class="tio-mega-menu__two-step-mobile-header">
                            <button
                              type="button"
                              class="tio-mega-menu__two-step-back"
                              aria-label={{this.mobileBackLabel item.label}}
                              {{on "click" this.backMobileTwoStepGroup}}
                            >
                              <PhCaretRight @size={{15}} class="rotate-180" />
                              <span>{{item.label}}</span>
                            </button>
                          </div>

                          {{#let this.currentMobileTwoStepGroup as |selectedGroup|}}
                            {{#if selectedGroup}}
                              <div class="tio-mega-menu__two-step-mobile-detail">
                                <div class="tio-mega-menu__two-step-breadcrumb">
                                  <span>{{item.label}}</span>
                                  <span aria-hidden="true">/</span>
                                  <strong>{{this.twoStepGroupLabel selectedGroup}}</strong>
                                </div>
                                <div class="tio-mega-menu__two-step-detail-heading">
                                  <strong>{{this.twoStepGroupLabel selectedGroup}}</strong>
                                  <span>{{this.destinationCountLabel
                                      selectedGroup.items.length
                                    }}</span>
                                </div>
                                {{#each selectedGroup.items as |leaf|}}
                                  <LinkTo
                                    @route={{leaf.route}}
                                    class="tio-mega-menu__two-step-mobile-leaf"
                                    {{on "click" this.handleLinkClick}}
                                  >
                                    {{#let leaf.icon as |LeafIcon|}}
                                      <LeafIcon @size={{18}} class="tio-fg-disabled" />
                                    {{/let}}
                                    <div class="tio-fill-min">
                                      <span class="tio-mega-menu__title">{{leaf.label}}</span>
                                      <p class="tio-mega-menu__desc">{{leaf.description}}</p>
                                    </div>
                                    <PhCaretRight
                                      @size={{17}}
                                      class="tio-mega-menu__two-step-arrow"
                                      aria-hidden="true"
                                    />
                                  </LinkTo>
                                {{/each}}
                              </div>
                            {{/if}}
                          {{/let}}
                        {{else}}
                          <div class="tio-mega-menu__two-step-mobile-section">
                            <div class="tio-mega-menu__two-step-mobile-heading">
                              <strong>{{item.label}}</strong>
                              <span>Choose a section</span>
                            </div>
                            {{#each this.currentMobileTwoStepGroups as |group groupIndex|}}
                              <button
                                type="button"
                                class="tio-mega-menu__two-step-mobile-group"
                                {{on
                                  "keydown"
                                  (fn this.handleMobileTwoStepGroupKeyDown groupIndex)
                                }}
                                {{on "click" (fn this.selectMobileTwoStepGroup group)}}
                              >
                                <span>{{this.twoStepGroupLabel group}}</span>
                                <PhCaretRight @size={{17}} />
                              </button>
                            {{/each}}
                          </div>
                        {{/if}}
                      </div>
                    {{else}}
                      <div class="tio-mega-menu__mobile-section pb-2">
                        {{#each (getVisibleDropdownItems item.dropdown) as |dropdownItem|}}
                          {{#if dropdownItem.flyout}}
                            <div>
                              <button
                                type="button"
                                class="tio-row-between tio-mega-menu__mobile-row w-full px-4 py-3 pl-12 text-left transition-colors"
                                aria-expanded="{{if
                                  (eq this.mobileExpandedFlyout dropdownItem.label)
                                  'true'
                                  'false'
                                }}"
                                {{on "click" (fn this.toggleMobileFlyout dropdownItem.label)}}
                              >
                                <div class="tio-row">
                                  {{#let dropdownItem.icon as |DropdownIcon|}}
                                    <DropdownIcon @size={{18}} class="tio-fg-disabled" />
                                  {{/let}}
                                  <span
                                    class="tio-fg-secondary tio-text-sm-medium"
                                  >{{dropdownItem.label}}</span>
                                </div>
                                <PhCaretDown
                                  @size={{14}}
                                  class="tio-mega-menu__caret transition-transform
                                    {{if
                                      (eq this.mobileExpandedFlyout dropdownItem.label)
                                      'rotate-180'
                                    }}"
                                />
                              </button>

                              {{#if (eq this.mobileExpandedFlyout dropdownItem.label)}}
                                <div class="tio-mega-menu__mobile-subsection pb-1">
                                  {{#each dropdownItem.flyout as |flyoutItem|}}
                                    <LinkTo
                                      @route={{flyoutItem.route}}
                                      class="tio-row tio-mega-menu__mobile-row--deep px-4 py-2.5 pl-20 transition-colors"
                                      {{on "click" this.handleLinkClick}}
                                    >
                                      {{#let flyoutItem.icon as |FlyoutIcon|}}
                                        <FlyoutIcon @size={{16}} class="tio-fg-disabled" />
                                      {{/let}}
                                      <span
                                        class="tio-mega-menu__mobile-leaf-label"
                                      >{{flyoutItem.label}}</span>
                                    </LinkTo>
                                  {{/each}}
                                </div>
                              {{/if}}
                            </div>
                          {{else}}
                            <LinkTo
                              @route={{dropdownItem.route}}
                              class="tio-row tio-mega-menu__mobile-row px-4 py-3 pl-12 transition-colors"
                              {{on "click" this.handleLinkClick}}
                            >
                              {{#let dropdownItem.icon as |DropdownIcon|}}
                                <DropdownIcon @size={{18}} class="tio-fg-disabled" />
                              {{/let}}
                              <div class="flex-1">
                                <div class="tio-row-tight">
                                  <span
                                    class="tio-fg-secondary tio-text-sm-medium"
                                  >{{dropdownItem.label}}</span>
                                  {{#if dropdownItem.stat}}
                                    <span
                                      class="tio-mega-menu__badge text-xs px-1.5 py-0.5 rounded"
                                    >{{dropdownItem.stat}}</span>
                                  {{/if}}
                                </div>
                              </div>
                            </LinkTo>
                          {{/if}}
                        {{/each}}
                      </div>
                    {{/if}}
                  {{/if}}
                </div>
              {{else if item.route}}
                <LinkTo
                  @route={{item.route}}
                  class="tio-row tio-mega-menu__mobile-divider tio-mega-menu__nav-item-mobile px-4 py-3 transition-colors"
                  {{on "click" this.handleLinkClick}}
                >
                  {{#let item.icon as |Icon|}}
                    <Icon @size={{20}} class="tio-fg-muted" />
                  {{/let}}
                  <span class="tio-fg-primary font-medium">{{item.label}}</span>
                </LinkTo>
              {{else}}
                <button
                  type="button"
                  class="tio-row tio-mega-menu__mobile-divider tio-mega-menu__nav-item-mobile w-full px-4 py-3 transition-colors"
                  {{on "click" (fn this.handleActionClick item)}}
                >
                  {{#let item.icon as |Icon|}}
                    <Icon @size={{20}} class="tio-fg-muted" />
                  {{/let}}
                  <span class="tio-fg-primary font-medium">{{item.label}}</span>
                </button>
              {{/if}}
            {{/each}}
          </div>
        </div>
      {{/if}}
    </nav>
  </template>
}

export default MegaMenu;
